import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, DollarSign, TrendingUp, Percent, Save, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const Stat = ({ icon: Icon, label, value, sub, color = '#39FF14', testid }) => (
    <div data-testid={testid} style={{
        flex: '1 1 180px', minWidth: 170, background: 'rgba(255,255,255,0.03)',
        border: `1px solid ${color}44`, borderRadius: 14, padding: '16px 18px',
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color, fontWeight: 800, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            <Icon size={15} /> {label}
        </div>
        <div style={{ fontSize: 26, fontWeight: 900, color: '#fff', marginTop: 6 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: '#A0AAB2', marginTop: 2 }}>{sub}</div>}
    </div>
);

const BonusAnalyticsPanel = () => {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [a, c] = await Promise.all([
                axios.get(`${API}/admin/bonus-analytics?days=${days}`, auth()),
                axios.get(`${API}/admin/load-bonus-config`, auth()),
            ]);
            setData(a.data);
            setCfg(c.data);
        } catch (e) {
            toast.error('Failed to load bonus analytics');
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { load(); }, [load]);

    const saveCfg = async () => {
        setSaving(true);
        try {
            const body = {
                enabled: !!cfg.enabled,
                interval_hours: Number(cfg.interval_hours),
                wagering_multiplier: Number(cfg.wagering_multiplier),
                tiers: cfg.tiers,
            };
            const { data: updated } = await axios.post(`${API}/admin/load-bonus-config`, body, auth());
            setCfg(updated);
            toast.success('Load-bonus settings saved');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const maxBonus = data ? Math.max(...(data.series || []).map(s => Math.max(s.deposits, s.bonus)), 1) : 1;

    return (
        <div data-testid="bonus-analytics-panel" className="space-y-5">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div>
                    <h2 style={{ fontSize: 22, fontWeight: 900, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Gift size={22} style={{ color: '#39FF14' }} /> Bonus ROI
                    </h2>
                    <p style={{ fontSize: 13, color: '#A0AAB2' }}>Free Play paid out vs the real deposits that came in.</p>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                    {[7, 30, 90].map(d => (
                        <button key={d} data-testid={`bonus-range-${d}`} onClick={() => setDays(d)}
                            style={{
                                padding: '7px 14px', borderRadius: 999, fontWeight: 800, fontSize: 13, cursor: 'pointer',
                                background: days === d ? '#39FF14' : 'rgba(255,255,255,0.06)',
                                color: days === d ? '#052500' : '#cbd5d1', border: 'none',
                            }}>{d}d</button>
                    ))}
                    <button onClick={load} title="Refresh" style={{ padding: '7px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: 'none', color: '#cbd5d1', cursor: 'pointer' }}>
                        <RefreshCw size={15} />
                    </button>
                </div>
            </div>

            {loading || !data ? (
                <div style={{ color: '#A0AAB2', padding: 40, textAlign: 'center' }}>Loading…</div>
            ) : (
                <>
                    {/* KPI cards */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        <Stat icon={DollarSign} label="Real Deposits" value={money(data.total_deposits)} sub={`over ${data.period_days} days`} color="#39FF14" testid="stat-deposits" />
                        <Stat icon={Gift} label="Bonus Paid" value={money(data.total_bonus)} sub={`${data.bonus_pct_of_deposits}% of deposits`} color="#FFD700" testid="stat-bonus" />
                        <Stat icon={TrendingUp} label="ROI" value={data.roi_deposits_per_bonus_dollar ? `${data.roi_deposits_per_bonus_dollar}×` : '—'} sub="deposits per $1 bonus" color="#00FFFF" testid="stat-roi" />
                        <Stat icon={Percent} label="Net In" value={money(data.net)} sub="deposits − bonus" color="#FF4DB8" testid="stat-net" />
                    </div>

                    {/* Load bonus highlight */}
                    <div style={{ background: 'rgba(57,255,20,0.06)', border: '1px solid #39FF1444', borderRadius: 14, padding: '14px 18px' }}>
                        <span style={{ color: '#39FF14', fontWeight: 800 }}>Load Bonus (new):</span>{' '}
                        <span style={{ color: '#fff', fontWeight: 800 }}>{money(data.load_bonus.total)}</span>
                        <span style={{ color: '#A0AAB2' }}> paid across {data.load_bonus.count} grants</span>
                    </div>

                    {/* Breakdown by type */}
                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, overflow: 'hidden' }}>
                        <div style={{ padding: '12px 16px', fontWeight: 800, color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>Bonus payouts by type</div>
                        {(data.bonuses || []).length === 0 ? (
                            <div style={{ padding: 20, color: '#A0AAB2' }}>No bonuses paid in this period.</div>
                        ) : data.bonuses.map((b, i) => (
                            <div key={i} data-testid={`bonus-row-${b.type}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                <span style={{ color: '#e5eee9', fontWeight: 600 }}>{b.label}</span>
                                <span style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                                    <span style={{ color: '#A0AAB2', fontSize: 12 }}>{b.count}×</span>
                                    <span style={{ color: '#FFD700', fontWeight: 800, minWidth: 90, textAlign: 'right' }}>{money(b.total)}</span>
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* Mini daily bar chart: deposits (green) vs bonus (gold) */}
                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: '14px 16px' }}>
                        <div style={{ fontWeight: 800, color: '#fff', marginBottom: 10 }}>Daily deposits vs bonus</div>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 120 }}>
                            {(data.series || []).map((s, i) => (
                                <div key={i} title={`${s.date}\nDeposits ${money(s.deposits)}\nBonus ${money(s.bonus)}`}
                                    style={{ flex: 1, display: 'flex', flexDirection: 'column-reverse', gap: 1, minWidth: 2 }}>
                                    <div style={{ height: `${(s.deposits / maxBonus) * 100}%`, background: '#39FF14', borderRadius: '2px 2px 0 0' }} />
                                    <div style={{ height: `${(s.bonus / maxBonus) * 100}%`, background: '#FFD700', borderRadius: '2px 2px 0 0' }} />
                                </div>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: '#A0AAB2' }}>
                            <span><span style={{ color: '#39FF14' }}>■</span> Deposits</span>
                            <span><span style={{ color: '#FFD700' }}>■</span> Bonus</span>
                        </div>
                    </div>

                    {/* Load-bonus config editor */}
                    {cfg && (
                        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,255,255,0.25)', borderRadius: 14, padding: '16px 18px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                <div style={{ fontWeight: 900, color: '#00FFFF' }}>Load Bonus settings</div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#e5eee9', fontWeight: 700, fontSize: 13 }}>
                                    <input type="checkbox" data-testid="load-bonus-enabled" checked={!!cfg.enabled} onChange={e => setCfg({ ...cfg, enabled: e.target.checked })} />
                                    {cfg.enabled ? 'Enabled' : 'Disabled'}
                                </label>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end' }}>
                                <label style={{ fontSize: 12, color: '#A0AAB2' }}>Interval (hours)
                                    <input type="number" min="0" step="0.5" data-testid="load-bonus-interval" value={cfg.interval_hours}
                                        onChange={e => setCfg({ ...cfg, interval_hours: e.target.value })}
                                        style={{ display: 'block', marginTop: 4, width: 110, padding: '8px 10px', borderRadius: 8, background: '#0d1512', border: '1px solid #2a3a33', color: '#fff' }} />
                                </label>
                                <label style={{ fontSize: 12, color: '#A0AAB2' }}>Wagering (×)
                                    <input type="number" min="0" step="0.5" data-testid="load-bonus-wagering" value={cfg.wagering_multiplier}
                                        onChange={e => setCfg({ ...cfg, wagering_multiplier: e.target.value })}
                                        style={{ display: 'block', marginTop: 4, width: 110, padding: '8px 10px', borderRadius: 8, background: '#0d1512', border: '1px solid #2a3a33', color: '#fff' }} />
                                </label>
                            </div>
                            <div style={{ marginTop: 14 }}>
                                <div style={{ fontSize: 12, color: '#A0AAB2', marginBottom: 6 }}>Tiers (min load → reward)</div>
                                {(cfg.tiers || []).map((t, i) => (
                                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                                        <span style={{ color: '#A0AAB2', fontSize: 12 }}>Load ≥ $</span>
                                        <input type="number" data-testid={`tier-min-${i}`} value={t.min}
                                            onChange={e => { const tiers = [...cfg.tiers]; tiers[i] = { ...t, min: Number(e.target.value) }; setCfg({ ...cfg, tiers }); }}
                                            style={{ width: 90, padding: '6px 8px', borderRadius: 8, background: '#0d1512', border: '1px solid #2a3a33', color: '#fff' }} />
                                        <span style={{ color: '#A0AAB2', fontSize: 12 }}>→</span>
                                        {t.pct != null ? (
                                            <>
                                                <input type="number" step="0.01" data-testid={`tier-pct-${i}`} value={t.pct}
                                                    onChange={e => { const tiers = [...cfg.tiers]; tiers[i] = { min: t.min, pct: Number(e.target.value) }; setCfg({ ...cfg, tiers }); }}
                                                    style={{ width: 90, padding: '6px 8px', borderRadius: 8, background: '#0d1512', border: '1px solid #2a3a33', color: '#fff' }} />
                                                <span style={{ color: '#A0AAB2', fontSize: 12 }}>× load (e.g. 0.20 = 20%)</span>
                                            </>
                                        ) : (
                                            <>
                                                <span style={{ color: '#A0AAB2', fontSize: 12 }}>+$</span>
                                                <input type="number" step="1" data-testid={`tier-amount-${i}`} value={t.amount}
                                                    onChange={e => { const tiers = [...cfg.tiers]; tiers[i] = { min: t.min, amount: Number(e.target.value) }; setCfg({ ...cfg, tiers }); }}
                                                    style={{ width: 90, padding: '6px 8px', borderRadius: 8, background: '#0d1512', border: '1px solid #2a3a33', color: '#fff' }} />
                                                <span style={{ color: '#A0AAB2', fontSize: 12 }}>flat Free Play</span>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <button onClick={saveCfg} disabled={saving} data-testid="load-bonus-save"
                                style={{ marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10, background: '#39FF14', color: '#052500', fontWeight: 900, border: 'none', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                                <Save size={16} /> {saving ? 'Saving…' : 'Save settings'}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default BonusAnalyticsPanel;
